<template>

<SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

<!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb provider-add-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/provider" class="breadcrumb sba-collection-crumb teal-text">
          <span>Providers Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text">
          <span>Add Provider</span>
        </span>
        <span class="sba-collection-context">Provider Onboarding</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="container provider-add-page-shell">
    <div class="provider-add-workbench sba-slide-in">
      <section class="provider-add-head">
        <div class="provider-add-title-block">
          <label class="active teal-text">Provider Desk</label>
          <h5>Add Provider</h5>
          <p>Capture provider identity, contact details, plan band, and optional tariff upload in one controlled workspace.</p>
        </div>
      </section>

      <LoadingDiv v-if="!isReady" />

      <section class="provider-add-body" v-else>
        <form @submit.prevent="handleSubmit" class="provider-add-form">
          <section class="provider-add-card">
            <div class="provider-add-section-head">
              <div>
                <label class="active teal-text">Provider Profile</label>
                <h6>Identity and contact details</h6>
              </div>
              <span>Required</span>
            </div>

            <div class="provider-add-grid">
              <div class="provider-add-field">
                <div class="input-field">
                  <input id="name" type="text" v-model="name" class="validate" required />
                  <label for="name">Provider Name</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <input id="md" type="text" v-model="md" class="validate" required />
                  <label for="md">MD's Name</label>
                </div>
              </div>

              <div class="provider-add-field is-wide">
                <div class="input-field">
                  <input id="address" type="text" v-model="address" class="validate" required />
                  <label for="address">Provider Address</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <input id="phone" type="text" v-model="phone" class="validate" required />
                  <label for="phone">Provider Phone</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <input
                    id="email"
                    name="provider-add-email"
                    type="email"
                    v-model="email"
                    class="validate"
                    autocomplete="new-password"
                    autocapitalize="none"
                    autocorrect="off"
                    spellcheck="false"
                    required
                  />

                  <div class="progress provider-add-inline-progress" v-if="isChecking">
                    <div class="indeterminate"></div>
                  </div>

                  <label for="email" :class="{ active: email }">Provider Email</label>
                  <p class="provider-add-checking" v-if="email && isChecking">
                    Checking email availability...
                  </p>
                  <p class="provider-add-warning" v-else-if="email && !isChecking && checkedEmailCounter > 0">
                    Sorry, email already belongs to another provider.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section class="provider-add-card">
            <div class="provider-add-section-head">
              <div>
                <label class="active teal-text">Plan and Tariff</label>
                <h6>Provider operating setup</h6>
              </div>
              <span>Setup</span>
            </div>

            <div class="provider-add-grid">
              <div class="provider-add-field">
                <div class="input-field">
                  <select v-model="plan">
                    <option value="" disabled selected>Select HMO Plan</option>
                    <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                      {{ plan.name }}
                    </option>
                  </select>
                  <label>Type of Plan (Enrollees)</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="file-field input-field provider-add-file-field">
                  <div class="btn grey darken-1">
                    <span>Add Custom Tariff</span>
                    <input type="file" @change="handleFile" />
                  </div>
                  <div class="file-path-wrapper">
                    <input class="file-path" v-model="inputFile" type="text" />
                  </div>
                </div>
                <p class="provider-add-warning" v-if="fileError">{{ fileError }}</p>
              </div>
            </div>
          </section>

          <p class="provider-add-warning is-block" v-if="uploadError">{{ uploadError }}</p>

          <section class="provider-add-action-card">
            <div>
              <span>Create provider account</span>
              <small>The provider will be assigned to active provider modules and schemes after submission.</small>
            </div>
            <span class="provider-add-error" v-if="error">{{ error }}</span>
            <button
              class="waves-effect waves-light btn-large provider-add-submit"
              :class="{
                disabled:
                  !name ||
                  !md ||
                  !address ||
                  !phone ||
                  !email ||
                  !plan ||
                  !allModules?.length ||
                  !allSchemes?.length ||
                  checkedEmailCounter > 0 ||
                  isChecking ||
                  isSubmitting,
              }"
            >
              <i class="material-icons left" v-if="!isSubmitting">check_circle</i>
              {{ isSubmitting ? `Submitting (${uploadProgress})` : "Add Provider" }}
            </button>
          </section>
        </form>
      </section>
    </div>
  </div>
</template>


<script>
import { ref, onMounted, onUpdated, computed, watchEffect } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import useStorage from "@/composables/useStorage";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import {
  debounce,
  getCachedPayload,
  hasBasicEmailShape,
  setCachedPayload,
} from "@/composables/requestOptimization";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { addModules } from "@/composables/cache/modules";
import { adminAPI } from '@/laravel/config';
import M from "materialize-css";


export default {
  name: "providerAdd",
  components: { SubHeading, LoadingDiv },
  setup() {
    const store = useStore();
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const response = ref(null);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const isChecking = ref(false);
    const error = ref(null);
    const name = ref(null);
    const md = ref(null);
    const address = ref(null);
    const phone = ref(null);
    const email = ref(null);
    const checkedEmailCounter = ref(1); // default to 1 to disable next button
    const plan = ref(null);
    const allModules = ref([]);
    const allSchemes = ref([]);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // Start
    onMounted(() => {
      // page name
      document.title = "Add Provider";
    });

    // Modules and Schemes
    onMounted(() => {
      // get modules
      allModules.value = addModules()
        .filter(x => x.type === "provider")
        .map(item => item.to);

      // get schemes
      (async () => {
        const { res } = await getItems("scheme");

        if (res.value) {
          allSchemes.value = res.value.data
            .map(item => item.id);
        } else {
          error.value = "ERROR: Important data not loaded!";
        }

      })();
    });

    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: null, url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: "sync", url: "providerSync", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Add"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: tab?.url === "providerAdd" });
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = "ERROR: Plans are not loaded!";
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // starter
    onMounted(() => {
      // get plans
      getItemsFromApi("plan/private");
    });

    // drop down
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      isSubmitting.value = false;
      uploadProgress.value = "0%";
    };

    const normalizedEmail = computed(() => {
      return email.value ? email.value.trim().toLowerCase() : null;
    });
    const emailCheckCacheTtl = 60 * 1000;
    let emailCheckRequestId = 0;

    const runEmailAvailabilityCheck = debounce(async (currentEmail, requestId) => {
      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      const cacheKey = `provider-email-check:${currentEmail}`;
      const cachedCount = getCachedPayload(cacheKey);

      if (cachedCount !== null) {
        checkedEmailCounter.value = cachedCount;
        isChecking.value = false;
        return;
      }

      const { res } = await getItems(`provider/email/checker?email=${encodeURIComponent(currentEmail)}`);

      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      if (res.value) {
        checkedEmailCounter.value = res.value.data;
        setCachedPayload(cacheKey, res.value.data, emailCheckCacheTtl);
      } else {
        checkedEmailCounter.value = 1;
      }

      isChecking.value = false;
    });

    // file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    const handleSubmit = async () => {
      if (isChecking.value || checkedEmailCounter.value > 0) {
        return;
      }

      // Start Uploading
      isSubmitting.value = true;

      // sort default Schemes and Modules
      let schemes = allSchemes.value;
      let modules = allModules.value;
      let providerEmail = normalizedEmail.value;

      // check file or no file creation
      if (file.value) {
        uploadError.value = null;

        // Cloud Function
        const fx = "tariffLoader";

        // Start
        let schedule = {
          plan_id: plan.value,
          type: "c", // c=customized, s=standard
        };

        // Continue
        let newProvider = {
          schedule_id: null,
          name: name.value,
          md: md.value,
          email: providerEmail,
          phone: phone.value,
          address: address.value,
        };

        // Round up
        const cloudInput = {
          schedule: schedule,
          schemes: schemes,
          modules: modules,
          provider: null,
          newProvider: newProvider,
          endPoint: `${adminAPI}/schedule/tariffs/bulk`,
          authToken: store.state?.user?.token || null,
        };

        // upload
        uploadFile(file.value, fx, cloudInput);
      } else {
        // Submit Single
        uploadProgress.value = "100%"; // so its not blank
        // start
        let newProvider = {
          schedule_id: null,
          name: name.value,
          md: md.value,
          email: providerEmail,
          phone: phone.value,
          address: address.value,
        };

        // round up
        const newVal = {
          schemes: schemes,
          modules: modules,
          plan: plan.value, // server will find applicable standard schedule
          provider: newProvider,
        };

        // post item
        const { res, err } = await postItem("provider/add", newVal);

        if (res.value) {
          // final
          router.push({
            name: "providerView",
            params: { id: res.value.data.id },
          });

          // clear all
          clearForm();
        } else {
          error.value = err.value;
          isSubmitting.value = false;
        }
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        //do stuff...
        // data.succ.Success, data.succ.inserted_rows, data.succ.schedule, data.succ.provider, data.succ.Error

        router.push({
          name: "providerView",
          params: { id: data.value.succ.provider.id },
        });

        // clear all
        clearForm();
      }
      if (uploadError.value) {
        // clear all
        clearForm();
      }
    });

    // watch upload
    watchEffect(() => {
      const currentEmail = normalizedEmail.value;

      if (!currentEmail) {
        checkedEmailCounter.value = 1;
        isChecking.value = false;
        emailCheckRequestId++;
        return;
      }

      if (!hasBasicEmailShape(currentEmail)) {
        checkedEmailCounter.value = 1;
        isChecking.value = false;
        emailCheckRequestId++;
        return;
      }

      // search email
      isChecking.value = true;
      checkedEmailCounter.value = 0;
      emailCheckRequestId++;
      runEmailAvailabilityCheck(currentEmail, emailCheckRequestId);
    });

    // output results
    const plans = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      handleFile,
      handleSubmit,
      allModules,
      allSchemes,
      isReady,
      error,
      name,
      md,
      address,
      phone,
      email,
      checkedEmailCounter,
      isSubmitting,
      isChecking,
      plans,
      plan,
      fileError,
      uploadError,
      file,
      uploadProgress,
      inputFile,
    };
  },
};
</script>

<style scoped>
.provider-add-breadcrumb {
  background: #fafafa !important;
  position: relative;
}

.provider-add-breadcrumb::after {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  height: 1px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.provider-add-page-shell {
  margin-top: -12px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.provider-add-workbench {
  color: #263238;
}

.provider-add-head,
.provider-add-body,
.provider-add-card,
.provider-add-action-card,
.provider-add-field {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.provider-add-head {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  margin-bottom: 0;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.provider-add-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.provider-add-title-block {
  position: relative;
  z-index: 2;
}

.provider-add-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.provider-add-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 780px;
}

.provider-add-body {
  background: #f7f9f9;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  padding: 22px 18px 18px;
  position: relative;
  z-index: 1;
}

.provider-add-form {
  display: grid;
  gap: 16px;
  margin: 0;
}

.provider-add-card,
.provider-add-action-card {
  background: #ffffff;
  border: 1px solid #dde6e9;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  overflow: visible;
}

.provider-add-section-head {
  align-items: center;
  border-bottom: 1px solid #edf2f3;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 22px 14px;
}

.provider-add-section-head label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 7px;
}

.provider-add-section-head h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
}

.provider-add-section-head span {
  background: #e8f5f3;
  border: 1px solid #b8dfda;
  border-radius: 999px;
  color: #00695c;
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 800;
  padding: 6px 10px;
}

.provider-add-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px 20px 20px;
}

.provider-add-field {
  background: #fbfcfc;
  border: 1px solid #e3eaed;
  border-radius: 6px;
  padding: 16px 18px 10px;
}

.provider-add-field.is-wide {
  grid-column: 1 / -1;
}

.provider-add-field :deep(.input-field),
.provider-add-file-field {
  margin-bottom: 0.45rem;
  margin-top: 0.35rem;
}

.provider-add-field :deep(input:not([type])),
.provider-add-field :deep(input[type=text]:not(.browser-default)),
.provider-add-field :deep(input[type=email]:not(.browser-default)),
.provider-add-field :deep(input[type=number]:not(.browser-default)) {
  color: #263238;
  margin-bottom: 0;
}

.provider-add-field :deep(input:not([type]):focus:not([readonly])),
.provider-add-field :deep(input[type=text]:not(.browser-default):focus:not([readonly])),
.provider-add-field :deep(input[type=email]:not(.browser-default):focus:not([readonly])),
.provider-add-field :deep(input[type=number]:not(.browser-default):focus:not([readonly])) {
  border-bottom-color: #00897b;
  box-shadow: 0 1px 0 0 #00897b;
}

.provider-add-file-field .btn {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

.provider-add-field .input-field input:-webkit-autofill,
.provider-add-field .input-field input:-webkit-autofill:hover,
.provider-add-field .input-field input:-webkit-autofill:focus {
  -webkit-text-fill-color: #263238;
  box-shadow: 0 0 0 1000px #fbfcfc inset;
  transition: background-color 9999s ease-out 0s;
}

.provider-add-inline-progress {
  background-color: #e8f3f2;
  border-radius: 0;
  height: 3px;
  left: 0;
  margin: 0;
  position: absolute;
  right: 0;
  top: 44px;
  width: 100%;
  z-index: 2;
}

.provider-add-inline-progress .indeterminate {
  background-color: #00897b;
}

.provider-add-checking {
  color: #78909c;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.25;
  margin: 10px 0 0;
}

.provider-add-warning,
.provider-add-error {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 999px;
  color: #b71c1c;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 10px 0 0;
  padding: 7px 10px;
}

.provider-add-warning.is-block {
  border-radius: 6px;
  margin: 0;
  max-width: max-content;
}

.provider-add-action-card {
  align-items: center;
  background: #ffffff;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 20px;
}

.provider-add-action-card span:not(.provider-add-error) {
  color: #263238;
  display: block;
  font-weight: 700;
}

.provider-add-action-card small {
  color: #607d8b;
  display: block;
  line-height: 1.45;
  margin-top: 4px;
}

.provider-add-submit {
  background: #00897b;
  border-radius: 5px;
  box-shadow: 0 10px 18px -16px rgba(38, 50, 56, 0.8);
  flex: 0 0 auto;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.provider-add-submit:hover {
  background: #00695c;
}

.pointer {
  cursor: default;
}

@media only screen and (max-width: 800px) {
  .provider-add-grid {
    grid-template-columns: 1fr;
  }

  .provider-add-action-card {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media only screen and (max-width: 600px) {
  .provider-add-page-shell {
    width: 94%;
  }

  .provider-add-head {
    padding: 20px;
  }

  .provider-add-title-block h5 {
    font-size: 1.45rem;
  }

  .provider-add-title-block p {
    font-size: 0.95rem;
  }

  .provider-add-body {
    padding: 18px 12px;
  }

  .provider-add-grid {
    padding-left: 14px;
    padding-right: 14px;
  }

  .provider-add-section-head,
  .provider-add-action-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-add-section-head span,
  .provider-add-submit,
  .provider-add-file-field .btn,
  .provider-add-warning,
  .provider-add-error {
    justify-content: center;
    width: 100%;
  }

  .provider-add-field {
    padding: 14px 14px 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .provider-add-head,
  .provider-add-body,
  .provider-add-card,
  .provider-add-action-card,
  .provider-add-field {
    animation: none;
  }
}
</style>
